import React, { useEffect, useState, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import DatePickerDMY from './DatePickerDMY';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Label } from './ui/label';

/**
 * DateModeSelector — unified date filter with 3 modes:
 *  - 'date'  : a single specific date (from = to = that date)
 *  - 'month' : an entire calendar month (from = 1st, to = last day)
 *  - 'range' : custom From / To
 *  - 'all'   : (optional, only when allowAll is true) no date filter (emits ''/'')
 *
 * Emits `{ from, to }` ISO strings (`YYYY-MM-DD`) via `onChange`.
 * Empty strings mean "no bound" (only happens in 'all' mode or when value is missing).
 *
 * Controlled mode: parent passes `mode`, `setMode`, `from`, `to`, and `onChange`.
 *   onChange receives the new `{ from, to }` whenever the user changes anything.
 */
export default function DateModeSelector({
  mode,
  setMode,
  from,
  to,
  onChange,
  allowAll = false,
  testIdPrefix = 'datemode',
  showLabels = true,
  size = 'sm', // 'sm' (h-8) or 'md' (h-9)
}) {
  const { t } = useLanguage();
  const h = size === 'md' ? 'h-9' : 'h-8';

  // ---- Local controls for "date" and "month" modes -------------------------
  // We keep small local state so the user can pick values progressively
  // without each keystroke immediately broadcasting an invalid range.
  const todayISO = new Date().toISOString().split('T')[0];

  const [singleDate, setSingleDate] = useState(() => from || todayISO);
  const [monthVal, setMonthVal] = useState(() => {
    const src = from || todayISO;
    return parseInt(src.split('-')[1] || (new Date().getMonth() + 1), 10);
  });
  const [yearVal, setYearVal] = useState(() => {
    const src = from || todayISO;
    return parseInt(src.split('-')[0] || new Date().getFullYear(), 10);
  });

  const lastDayOf = (y, m) => new Date(y, m, 0).getDate();
  const pad = (n) => String(n).padStart(2, '0');

  // Sync incoming from/to back into local controls when mode/value change
  // externally (e.g., parent resets). Only react to mode swap or value diff.
  useEffect(() => {
    if (mode === 'date' && from) setSingleDate(from);
    if (mode === 'month' && from) {
      const [y, m] = from.split('-');
      if (y) setYearVal(parseInt(y, 10));
      if (m) setMonthVal(parseInt(m, 10));
    }
  }, [mode, from]);

  // Broadcast helper
  const emit = useCallback((next) => {
    if (onChange) onChange(next);
  }, [onChange]);

  // ---- Handlers ------------------------------------------------------------
  const handleModeChange = (m) => {
    setMode(m);
    if (m === 'date') {
      const d = singleDate || todayISO;
      setSingleDate(d);
      emit({ from: d, to: d });
    } else if (m === 'month') {
      const last = lastDayOf(yearVal, monthVal);
      emit({ from: `${yearVal}-${pad(monthVal)}-01`, to: `${yearVal}-${pad(monthVal)}-${pad(last)}` });
    } else if (m === 'range') {
      // Keep the parent's current range (or default to current month if empty)
      if (!from && !to) {
        const y = yearVal, mo = monthVal;
        const last = lastDayOf(y, mo);
        emit({ from: `${y}-${pad(mo)}-01`, to: `${y}-${pad(mo)}-${pad(last)}` });
      } else {
        emit({ from: from || '', to: to || '' });
      }
    } else if (m === 'all') {
      emit({ from: '', to: '' });
    }
  };

  const onSingleDate = (iso) => {
    setSingleDate(iso);
    emit({ from: iso, to: iso });
  };

  const onMonthChange = (m) => {
    const mi = parseInt(m, 10);
    setMonthVal(mi);
    const last = lastDayOf(yearVal, mi);
    emit({ from: `${yearVal}-${pad(mi)}-01`, to: `${yearVal}-${pad(mi)}-${pad(last)}` });
  };
  const onYearChange = (y) => {
    const yi = parseInt(y, 10);
    if (!yi) return;
    setYearVal(yi);
    const last = lastDayOf(yi, monthVal);
    emit({ from: `${yi}-${pad(monthVal)}-01`, to: `${yi}-${pad(monthVal)}-${pad(last)}` });
  };

  const onRangeFrom = (iso) => emit({ from: iso, to: to || '' });
  const onRangeTo = (iso) => emit({ from: from || '', to: iso });

  // ---- Render --------------------------------------------------------------
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid={`${testIdPrefix}-wrapper`}>
      <div>
        {showLabels && <Label className="text-xs">{t('dateMode') || 'Period'}</Label>}
        <Select value={mode} onValueChange={handleModeChange}>
          <SelectTrigger className={`rounded-none ${h} w-32 text-xs`} data-testid={`${testIdPrefix}-mode`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {allowAll && <SelectItem value="all">{t('allTime') || 'All time'}</SelectItem>}
            <SelectItem value="date">{t('modeDate') || 'Date'}</SelectItem>
            <SelectItem value="month">{t('modeMonth') || 'Month'}</SelectItem>
            <SelectItem value="range">{t('modeRange') || 'Range'}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {mode === 'date' && (
        <div>
          {showLabels && <Label className="text-xs">{t('date')}</Label>}
          <DatePickerDMY
            value={singleDate}
            onChange={onSingleDate}
            className={`rounded-none ${h} w-36 text-xs font-mono`}
            data-testid={`${testIdPrefix}-date`}
          />
        </div>
      )}

      {mode === 'month' && (
        <>
          <div>
            {showLabels && <Label className="text-xs">{t('month')}</Label>}
            <Select value={String(monthVal)} onValueChange={onMonthChange}>
              <SelectTrigger className={`rounded-none ${h} w-32 text-xs`} data-testid={`${testIdPrefix}-month`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 12 }, (_, i) => (
                  <SelectItem key={i} value={String(i + 1)}>
                    {new Date(2000, i).toLocaleString('en', { month: 'long' })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            {showLabels && <Label className="text-xs">{t('year')}</Label>}
            <Input
              type="number"
              value={yearVal}
              onChange={(e) => onYearChange(e.target.value)}
              className={`rounded-none ${h} w-20 text-xs`}
              data-testid={`${testIdPrefix}-year`}
            />
          </div>
        </>
      )}

      {mode === 'range' && (
        <>
          <div>
            {showLabels && <Label className="text-xs">{t('fromDate')}</Label>}
            <DatePickerDMY
              value={from || ''}
              onChange={onRangeFrom}
              className={`rounded-none ${h} w-36 text-xs font-mono`}
              data-testid={`${testIdPrefix}-from`}
            />
          </div>
          <div>
            {showLabels && <Label className="text-xs">{t('toDate')}</Label>}
            <DatePickerDMY
              value={to || ''}
              onChange={onRangeTo}
              className={`rounded-none ${h} w-36 text-xs font-mono`}
              data-testid={`${testIdPrefix}-to`}
            />
          </div>
        </>
      )}
    </div>
  );
}
